import React, { useState } from "react";
import { t, currentLanguage } from "../../i18n";
import AnimatedSection from "../AnimatedSection/AnimatedSection";
import PhoneInput from "react-phone-number-input";
import "react-phone-number-input/style.css";
import styles from "./ContactSection.module.css";
import axios from "axios";
// @ts-ignore
import CryptoJS from "crypto-js";
import { Dropdown } from "primereact/dropdown";
import { trackLeadFormSuccess } from "../../lib/analytics";
import "primereact/resources/themes/lara-light-indigo/theme.css";
import "primereact/resources/primereact.min.css";

const ContactSection: React.FC = () => {
  const [phone, setPhone] = useState("");
  const [employeeCount, setEmployeeCount] = useState("");
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [personalDataChecked, setPersonalDataChecked] = useState(false);
  const [errors, setErrors] = useState<{ [key: string]: string }>({});
  const [formData, setFormData] = useState({
    name: "",
    lastname: "",
    email: "",
    position: "",
    organizationname: "",
    text: "",
  });

  const employeeCountOptions = [
    { label: "1 - 500", value: "1-500" },
    { label: "501 - 3.000", value: "501-3.000" },
    { label: "3.001 - 5.000", value: "3.001-5.000" },
    { label: "5.001 - 10.000", value: "5.001-10.000" },
    { label: "10.000+", value: "10.000+" },
  ];

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
    setErrors((prev) => ({ ...prev, [name]: "" }));
  };

  const validate = () => {
    const newErrors: { [key: string]: string } = {};
    if (!formData.name.trim()) newErrors.name = t("contactSection.errors.name");
    if (!formData.lastname.trim()) newErrors.lastname = t("contactSection.errors.lastname");
    if (!formData.email.trim() || !/\S+@\S+\.\S+/.test(formData.email)) newErrors.email = t("contactSection.errors.email");
    if (!phone) newErrors.phone = t("contactSection.errors.phone");
    if (!formData.position.trim()) newErrors.position = t("contactSection.errors.position");
    if (!formData.organizationname.trim()) newErrors.organizationname = t("contactSection.errors.organization");
    if (!employeeCount) newErrors.employeeCount = t("contactSection.errors.employeeCount");
    if (!formData.text.trim()) newErrors.text = t("contactSection.errors.text");
    if (!personalDataChecked) newErrors.personalData = t("contactSection.errors.personalData");
    return newErrors;
  };

  const fieldOrder = ["name", "lastname", "email", "phone", "position", "organizationname", "employeeCount", "text", "personalData"];

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const validationErrors = validate();
    if (Object.keys(validationErrors).length > 0) {
      setErrors(validationErrors);
      const firstErrorKey = fieldOrder.find((key) => validationErrors[key]);
      if (firstErrorKey) {
        const el = document.getElementById(`field-${firstErrorKey}`);
        if (el) {
          const top = el.getBoundingClientRect().top + window.scrollY - 100;
          window.scrollTo({ top, behavior: "smooth" });
        }
      }
      return;
    }

    const timestamp = Math.floor(Date.now() / 1000);
    const stringToSign = `${formData.email}:${timestamp}`;
    const signature = CryptoJS.HmacSHA256(stringToSign, process.env.REACT_APP_SECRET_KEY || "").toString(CryptoJS.enc.Hex);

    const body = {
      TranData: {
        date: new Date().toISOString().split("T")[0],
        email: formData.email,
        name: formData.name,
        lastname: formData.lastname,
        phonenumber: phone,
        position: formData.position,
        organizationname: formData.organizationname,
        employeecount: employeeCount,
        text: formData.text,
      },
      LangaugeCode: `${currentLanguage}-${currentLanguage.toUpperCase()}`,
      TenantCode: "SENSE",
      Signature: signature,
      Timestamp: timestamp,
    };

    try {
      const response = await axios.post(process.env.REACT_APP_SEND_MAIL_ENDPOINT || "", body, {
        headers: {
          "Content-Type": "application/json",
          TenantCode: "SENSE",
          UserCode: process.env.REACT_APP_USER_CODE || "",
        },
      });

      if (response.status === 200) {
        trackLeadFormSuccess();
        setIsSubmitted(true);
      }
    } catch (error) {
      console.error("Hata:", error);
    }
  };

  return (
    <section className="w-full py-20 px-6 md:px-20 bg-gray-50 scroll-mt-[80px]" id="contact">
      <div className="max-w-8xl mx-auto grid grid-cols-1 md:grid-cols-2 gap-12 items-stretch">
        <AnimatedSection delay={200}>
          <div className="h-full max-md:order-2">
            <img src="/images/contactMale.jpg" alt="İletişim Görseli" className="w-full h-full object-cover rounded-xl shadow-md max-md:h-64" />
          </div>
        </AnimatedSection>

        <AnimatedSection delay={400}>
          <div className="bg-white p-8 rounded-xl shadow-md flex flex-col justify-between max-md:p-4 max-md:order-1">
            <h2 className="text-4xl font-bold bg-gradient-to-r from-primaryGreen to-primaryPurple-light bg-clip-text text-transparent mb-6 max-md:text-2xl">
              {t("contactSection.title")}
            </h2>

            {!isSubmitted ? (
              <form noValidate onSubmit={handleSubmit} className="space-y-5 flex flex-col flex-grow">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div id="field-name">
                    <input
                      type="text"
                      name="name"
                      value={formData.name}
                      onChange={handleChange}
                      placeholder={t("contactSection.name")}
                      className="border border-gray-300 rounded-md p-3 w-full"
                    />
                    {errors.name && <p className="text-sm text-red-600">{errors.name}</p>}
                  </div>
                  <div id="field-lastname">
                    <input
                      type="text"
                      name="lastname"
                      value={formData.lastname}
                      onChange={handleChange}
                      placeholder={t("contactSection.surname")}
                      className="border border-gray-300 rounded-md p-3 w-full"
                    />
                    {errors.lastname && <p className="text-sm text-red-600">{errors.lastname}</p>}
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div id="field-email">
                    <input
                      type="email"
                      name="email"
                      value={formData.email}
                      onChange={handleChange}
                      placeholder={t("contactSection.email")}
                      className="border border-gray-300 rounded-md p-3 w-full"
                    />
                    {errors.email && <p className="text-sm text-red-600">{errors.email}</p>}
                  </div>
                  <div id="field-phone">
                    <PhoneInput
                      international
                      defaultCountry="TR"
                      value={phone}
                      onChange={(value) => {
                        setPhone(value || "");
                        setErrors((prev) => ({ ...prev, phone: "" }));
                      }}
                      placeholder={t("contactSection.phone")}
                      className={styles.phoneInput}
                      inputClassName={styles.phoneInputInput}
                    />
                    {errors.phone && <p className="text-sm text-red-600">{errors.phone}</p>}
                  </div>
                </div>

                <div id="field-position">
                  <input
                    type="text"
                    name="position"
                    value={formData.position}
                    onChange={handleChange}
                    placeholder={t("contactSection.position")}
                    className="border border-gray-300 rounded-md p-3 w-full"
                  />
                  {errors.position && <p className="text-sm text-red-600">{errors.position}</p>}
                </div>

                <div id="field-organizationname">
                  <input
                    type="text"
                    name="organizationname"
                    value={formData.organizationname}
                    onChange={handleChange}
                    placeholder={t("contactSection.organization")}
                    className="border border-gray-300 rounded-md p-3 w-full"
                  />
                  {errors.organizationname && <p className="text-sm text-red-600">{errors.organizationname}</p>}
                </div>

                <div id="field-employeeCount" className="relative z-10 pb-2">
                  <Dropdown
                    value={employeeCount}
                    onChange={(e) => {
                      setEmployeeCount(e.value);
                      setErrors((prev) => ({ ...prev, employeeCount: "" }));
                    }}
                    options={employeeCountOptions}
                    placeholder={t("contactSection.employeeCount")}
                    className="w-full"
                  />
                  {errors.employeeCount && <p className="text-sm text-red-600">{errors.employeeCount}</p>}
                </div>

                <div id="field-text">
                  <textarea
                    name="text"
                    value={formData.text}
                    onChange={handleChange}
                    placeholder={t("contactSection.message")}
                    rows={5}
                    className="border border-gray-300 rounded-md p-3 w-full resize-none"
                  />
                  {errors.text && <p className="text-sm text-red-600">{errors.text}</p>}
                </div>

                <div className="flex items-start gap-2">
                  <input type="checkbox" className="mt-1" />
                  <label className="text-sm">
                    <a href="/documents/3-Iletisim-Formu-Acik-Riza-Metni.pdf" target="_blank" rel="noopener noreferrer" className="font-semibold">
                      {t("contactSection.openRiza")}
                    </a>{" "}
                    {t("contactSection.openRizaText")}
                  </label>
                </div>

                <div id="field-personalData" className="flex items-start gap-2">
                  <input
                    type="checkbox"
                    required
                    className="mt-1"
                    checked={personalDataChecked}
                    onChange={(e) => {
                      setPersonalDataChecked(e.target.checked);
                      setErrors((prev) => ({ ...prev, personalData: "" }));
                    }}
                  />
                  <label className="text-sm">
                    <a href="/documents/1-Site-Uyesi-Aydinlatma_v2.pdf" target="_blank" rel="noopener noreferrer" className="font-semibold">
                      {t("contactSection.personalData")}
                    </a>{" "}
                    {t("contactSection.personalDataText")}
                  </label>
                </div>
                {errors.personalData && <p className="text-sm text-red-600">{errors.personalData}</p>}

                <button
                  type="submit"
                  className="max-w-[300px] bg-gradient-to-r from-primaryGreen via-primaryGreen to-[#97DCFB] text-white px-6 py-3 rounded-xl text-lg font-semibold hover:opacity-90 transition-all"
                >
                  {t("contactSection.send")}
                </button>
              </form>
            ) : (
              <div className="flex flex-col items-center justify-center text-center py-8">
                <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mb-4">
                  <svg className="w-8 h-8 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                  </svg>
                </div>
                <h3 className="text-2xl font-bold text-gray-800 mb-2">{t("contactSection.successTitle")}</h3>
                <p className="text-gray-600 mb-6">{t("contactSection.successText")}</p>
              </div>
            )}
          </div>
        </AnimatedSection>
      </div>
    </section>
  );
};

export default ContactSection;
